import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  sendDeleteRequest,
  sendGetRequest,
  sendPostRequest,
} from '../../../api/requestUtil';

export const getBlogDetailsById = createAsyncThunk<
  BlogCard,
  string | number | undefined,
  { state: AppState; rejectValue: BlogPageDetailError }
>('get-blog-details-by-id', async (blogId, { getState, rejectWithValue }) => {
  try {
    const token = getState().user.authToken;
    return await sendGetRequest(`/api/blogs/${blogId}/`, token);
  } catch (error: any) {
    return rejectWithValue({
      detail: error.response?.data?.detail || 'Failed to fetch blog details',
    });
  }
});

export const getBlogDiscussionById = createAsyncThunk(
  'get-blog-discussion-by-id',
  async (
    blogId: string | number | undefined
  ): Promise<BlogDiscussionsListSuccessType> => {
    return sendGetRequest(`/api/blogs/${blogId}/discussions/`);
  }
);

export const getBlogPostsById = createAsyncThunk<
  {
    posts: BlogPostType[];
    pagination: {
      page: number;
      page_size: number;
      total_count: number;
      total_pages: number;
      has_next: boolean;
      has_previous: boolean;
    };
  },
  { blogId: number; page?: number },
  { rejectValue: any }
>('get-blog-posts-by-id', async ({ blogId, page = 1 }, { rejectWithValue }) => {
  try {
    // Use the paginated endpoint
    const response = await sendGetRequest(
      `/api/blogs/${blogId}/posts/paginated/?page=${page}`
    );
    return response;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data || 'Failed to fetch blog posts'
    );
  }
});

export const verifyBlogUrl = createAsyncThunk(
  'verifyBlogUrl-blog-url',
  async (url: string): Promise<BlogCard> => {
    return sendGetRequest(`/api/blogs/url/?url=${url}`);
  }
);

export const uploadBlogImage = createAsyncThunk<
  UploadBlogImageSuccessType,
  FormData,
  { state: AppState; rejectValue: UploadBlogImageErrorType }
>(
  'upload-blog-image',
  async (body: FormData, { getState, rejectWithValue }) => {
    const state = getState();
    const token = state.user.authToken;
    try {
      const response = (await sendPostRequest(
        '/api/blogs/upload-image/',
        body,
        token
      )) as { 'blog-image-url': string };
      return {
        blogImageUrl: response['blog-image-url'],
      };
    } catch (error: any) {
      return rejectWithValue({
        detail: error.response?.data?.detail || 'Image upload failed',
      });
    }
  }
);

export const uploadEditBlog = createAsyncThunk<
  any,
  UploadBlogRequestType,
  { state: AppState; rejectValue: string }
>(
  'upload-blog',
  async (uploadBlogData, { getState, rejectWithValue }) => {
    const state = getState() as AppState;
    const token = state.user.authToken;
    try {
      return await sendPostRequest('/api/upload-edit-blog/', uploadBlogData, token);
    } catch (error: any) {
      const msg =
        error?.detail ||
        (Object.values(error ?? {}).flat() as string[])[0] ||
        'Failed to upload blog. Please try again.';
      return rejectWithValue(msg);
    }
  }
);

export const deleteBlog = createAsyncThunk(
  'delete-blog',
  async (body: any, { getState }) => {
    const state = getState() as AppState;
    const token = state.user.authToken;
    return sendDeleteRequest(
      `/api/blogs/delete-by-Id/?blogId=${body.blogId}`,
      token
    );
  }
);
